import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Type } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { FilingLoaderMode } from '../../../../environments/environment.types';
import { FILING_LOADER_VARIANTS } from './filing-loader.registry';

/**
 * Mounts the UI component paired with the currently active loader strategy.
 *
 * Resolved at construction time from `environment.filingLoaderMode` via the
 * registry. When the active variant has `ui: null` (e.g. the `'skeleton'`
 * mode), nothing is rendered — the container is kept visually clean and the
 * per-sheet spinner path remains the sole loading UX.
 *
 * Consumers mount a single `<app-filing-loader-outlet>` in the container
 * template. They never import a concrete UI component — the outlet does the
 * lookup, so the container stays unchanged when new variants are added.
 *
 * @see docs/ui/loaders/filing/README.md
 */
@Component({
  selector: 'app-filing-loader-outlet',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (ui) {
      <ng-container *ngComponentOutlet="ui"></ng-container>
    }
  `,
})
export class FilingLoaderOutletComponent {
  // Cast narrows the widened object-literal property back to its validated
  // union type (validated at write-time via `satisfies`).
  readonly ui: Type<unknown> | null =
    FILING_LOADER_VARIANTS[environment.filingLoaderMode as FilingLoaderMode].ui;
}
